import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';
import { track, POST_MATCH_PAGE } from '../utils/analytics';

const WORKFLOW = [
  'Add match context',
  'Add key moments and evidence',
  'Review the event timeline',
  'Generate the coaching report',
  'Track priorities across later matches',
];

const CAPABILITIES = [
  {
    title: 'Decisive moments',
    text: 'Identify the score changes and events that changed the match outcome.',
  },
  {
    title: 'Coaching priorities',
    text: 'Convert submitted evidence into clear areas for staff review.',
  },
  {
    title: 'Training responses',
    text: 'Connect match findings to practical coaching and training actions.',
  },
  {
    title: 'Player and role reviews',
    text: 'Document individual contributions and questions when evidence is supplied.',
  },
  {
    title: 'Match memory',
    text: 'Track recurring findings and coaching responses across matches.',
  },
  {
    title: 'Shareable reports',
    text: 'Provide coaches and staff with consistent web and PDF reports.',
  },
];

export default function ProductPostMatch() {
  usePageMeta(
    'Tactmark Post-Match | Football & Soccer Analysis After the Match',
    'Football and soccer post-match analysis software. Turn key match moments, coaching observations, and evidence into structured coaching reports and training actions.'
  );

  useEffect(() => {
    track(POST_MATCH_PAGE);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <div className="max-w-4xl mx-auto px-6 pt-16 pb-12 border-b border-gray-200">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">
          Tactmark Post-Match
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy-900 leading-tight mb-6">
          Turn every match into the next coaching decision.
        </h1>
        <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mb-8">
          Tactmark Post-Match turns match events, coaching observations, player reviews, and supporting evidence into structured reports that identify decisive moments, coaching priorities, and training responses.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <Link
            to="/login?mode=signup"
            className="bg-navy-900 hover:bg-navy-800 text-white font-medium px-6 py-3 rounded transition-colors"
          >
            Start free trial
          </Link>
          <Link
            to="/contact"
            className="border border-navy-900 text-navy-900 font-medium px-6 py-3 rounded hover:bg-navy-50 transition-colors"
          >
            Book a demo
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-14">
        <h2 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-6">
          Post-match analysis is often scattered across notes, clips, messages, and staff opinions.
        </h2>
        <p className="text-gray-600 leading-relaxed max-w-3xl">
          Tactmark gives coaching teams one structured workflow for documenting what happened, identifying what mattered, deciding what to change, and reviewing whether the response worked in later matches.
        </p>
      </div>

      <div className="bg-navy-900">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <p className="text-navy-400 text-xs font-semibold tracking-widest uppercase mb-4">Workflow</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-white mb-10">
            From match evidence to later-match review.
          </h2>
          <ol className="space-y-5">
            {WORKFLOW.map((step, index) => (
              <li key={step} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded bg-white/10 text-white text-sm font-semibold flex items-center justify-center">
                  {index + 1}
                </span>
                <span className="text-navy-100 pt-1.5">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="bg-slate-50 border-y border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">What coaches get</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-4">
            A coaching report from the evidence you submitted.
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-3xl mb-8">
            Match snapshot, decisive risk, strongest positive response, and the main training priority —
            structured so staff can act before the next session.
          </p>
          <figure className="m-0">
            <img
              src="/product-proof/post-match-desktop.png"
              alt="Tactmark Post-Match analysis report showing match snapshot, decisive risk, and training priority"
              className="w-full h-auto border border-gray-200 shadow-[0_18px_50px_rgba(18,33,64,0.08)]"
              width={1440}
              height={900}
            />
            <figcaption className="mt-3 text-sm text-gray-500">
              Tactmark Post-Match: Riverside FC vs Valley United — decisive risk and set-piece defending as the training priority.
            </figcaption>
          </figure>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-14">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Capability areas</p>
        <h2 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-8">
          Built for coaching decisions, not another report format.
        </h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {CAPABILITIES.map((item) => (
            <div key={item.title} className="border border-gray-200 rounded p-6">
              <h3 className="text-navy-900 font-semibold mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Evidence guardrail</p>
          <h2 className="font-serif text-2xl text-navy-900 mb-4">
            Works from the evidence your coaching team submits.
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-3xl mb-8">
            Tactmark works from the evidence and observations submitted by the coaching team. It does not claim to automatically interpret complete match footage.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/login?mode=signup"
              className="bg-navy-900 hover:bg-navy-800 text-white font-medium px-6 py-3 rounded transition-colors"
            >
              Start free trial
            </Link>
            <Link
              to="/products"
              className="border border-navy-900 text-navy-900 font-medium px-6 py-3 rounded hover:bg-navy-50 transition-colors"
            >
              View all products
            </Link>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
